import React, { useRef, useState } from "react";
import axios from "axios";
import { API } from "../auth/AuthContext";
import { API_REQUEST_TIMEOUT_MS, UPLOAD_REQUEST_TIMEOUT_MS, authRequestErrorMessage } from "../auth/authRequest";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { ShieldCheck, IdCard, Camera, Loader2, CheckCircle2, X, AlertTriangle, Clock } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { SITE_EMAILS } from "../lib/siteConfig";
import SelfieCapture from "./SelfieCapture";
import { compressImageForUpload, formatUploadError } from "../lib/compressImage";

const CAPTURE_CONFIG = {
  id_front: {
    title: "ID front",
    description: "Lay your driver's licence flat, fill the frame, and avoid glare or blur.",
    facingMode: "environment",
    filenamePrefix: "id_front",
    mirrorVideo: false,
    showFaceGuide: false,
    fallbackLabel: "Camera not working? Upload a photo of your ID front instead",
    liveLabel: "Take a live photo",
  },
  id_back: {
    title: "ID back",
    description: "Flip your licence and capture the back side.",
    facingMode: "environment",
    filenamePrefix: "id_back",
    mirrorVideo: false,
    showFaceGuide: false,
    fallbackLabel: "Camera not working? Upload a photo of your ID back instead",
    liveLabel: "Take a live photo",
  },
  selfie: {
    title: "Live selfie",
    description: "Look straight at the camera, remove glasses, ensure even lighting. We'll capture a single frame for liveness verification.",
    facingMode: "user",
    filenamePrefix: "selfie",
    mirrorVideo: true,
    showFaceGuide: true,
    fallbackLabel: "Camera not working? Upload a recent selfie instead",
    liveLabel: "Take a live selfie",
  },
};

const STEPS = [
  { key: "id_front", label: "Licence front", hint: "Driver's licence — front side", icon: IdCard, required: true, live: true },
  { key: "id_back",  label: "Licence back",  hint: "Same licence — back side", icon: IdCard, required: true, live: true },
  { key: "selfie",   label: "Selfie",   hint: "Tap to open camera — we'll capture a single frame for liveness", icon: Camera, required: true, live: true },
];

/**
 * Real Didit KYC modal:
 *   1. User captures each step via live camera (or file fallback).
 *   2. We POST each file to /api/upload, store the returned file_id.
 *   3. On submit, POST file_ids to /api/kyc/start which routes to Didit.
 *   4. Render approved / manual_review / declined accordingly.
 */
function UploadTile({ step, file, fileId, uploading, onPick, onClear, onLiveCapture }) {
  const Icon = step.icon;
  const ref = useRef(null);
  const captured = !!fileId;
  const isLive = step.live;
  const captureCfg = CAPTURE_CONFIG[step.key];
  return (
    <div
      className={
        "relative w-full h-44 rounded-xl border-2 border-dashed grid place-items-center transition cursor-pointer " +
        (captured
          ? "border-[var(--zc-success)] bg-[rgba(22,163,74,0.06)]"
          : "border-[var(--zc-border-2)] hover:border-[var(--zc-accent)] hover:bg-[var(--zc-accent-soft)]")
      }
      onClick={() => { if (isLive) onLiveCapture?.(); else ref.current?.click(); }}
      data-testid={`kyc-tile-${step.key}`}
    >
      {!isLive && (
        <input
          ref={ref}
          type="file"
          accept="image/*"
          capture={"environment"}
          className="hidden"
          aria-label={step.label}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) onPick(f);
            e.target.value = "";
          }}
          data-testid={`kyc-input-${step.key}`}
        />
      )}
      {captured && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onClear(); }}
          className="absolute top-2 right-2 w-6 h-6 rounded-full bg-white border grid place-items-center"
          data-testid={`kyc-clear-${step.key}`}
        >
          <X size={12} />
        </button>
      )}
      {isLive && !captured && !uploading && (
        <div className="absolute top-2 left-2 zc-badge px-2 py-0.5 text-white" style={{ background: "var(--zc-danger,#dc2626)" }}>
          ● Live
        </div>
      )}
      <div className="text-center px-3">
        {uploading ? (
          <>
            <Loader2 size={28} className="mx-auto animate-spin text-[var(--zc-accent)]" />
            <div className="text-sm font-semibold mt-2">Uploading…</div>
          </>
        ) : captured ? (
          <>
            <CheckCircle2 size={28} className="mx-auto text-[var(--zc-success)]" />
            <div className="text-sm font-semibold mt-2">{step.label} captured</div>
            <div className="zc-hint mt-1 truncate max-w-[180px] mx-auto">{file?.name}</div>
          </>
        ) : (
          <>
            <Icon size={28} className="mx-auto text-[var(--zc-text-3)]" />
            <div className="text-sm font-semibold mt-2">
              {isLive ? captureCfg?.liveLabel || "Take a live photo" : `${step.label}${step.required ? "" : " (optional)"}`}
            </div>
            <div className="zc-hint mt-1">{step.hint}</div>
          </>
        )}
      </div>
    </div>
  );
}

export default function KycModal({ open, onClose, onApproved }) {
  const [files, setFiles] = useState({ id_front: null, id_back: null, selfie: null });
  const [fileIds, setFileIds] = useState({ id_front: null, id_back: null, selfie: null });
  const [uploading, setUploading] = useState({ id_front: false, id_back: false, selfie: false });
  const [phase, setPhase] = useState("capture");
  const [result, setResult] = useState(null);
  const [captureKey, setCaptureKey] = useState(null);
  const requiredOk = !!fileIds.id_front && !!fileIds.id_back && !!fileIds.selfie;
  const activeCapture = captureKey ? CAPTURE_CONFIG[captureKey] : null;

  const pickFile = async (key, file) => {
    setUploading((u) => ({ ...u, [key]: true }));
    try {
      const compressed = await compressImageForUpload(file);
      setFiles((f) => ({ ...f, [key]: compressed }));
      const fd = new FormData();
      fd.append("file", compressed, compressed.name);
      const r = await axios.post(`${API}/upload`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
        timeout: UPLOAD_REQUEST_TIMEOUT_MS,
      });
      setFileIds((s) => ({ ...s, [key]: r.data.file_id }));
      return true;
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(
        detail ? formatUploadError(detail) : (e.message || authRequestErrorMessage(e)),
      );
      setFiles((f) => ({ ...f, [key]: null }));
      return false;
    } finally {
      setUploading((u) => ({ ...u, [key]: false }));
    }
  };

  const onCaptureConfirmed = async (blobOrFile, filename) => {
    const key = captureKey;
    setCaptureKey(null);
    if (!key) return;
    const f = blobOrFile instanceof File
      ? blobOrFile
      : new File([blobOrFile], filename || `${key}_${Date.now()}.jpg`, { type: blobOrFile.type || "image/jpeg" });
    const ok = await pickFile(key, f);
    if (!ok) return;
    if (key === "id_front") {
      window.setTimeout(() => setCaptureKey("id_back"), 200);
    } else if (key === "id_back") {
      window.setTimeout(() => setCaptureKey("selfie"), 200);
    }
  };

  const clearFile = (key) => {
    setFiles((f) => ({ ...f, [key]: null }));
    setFileIds((s) => ({ ...s, [key]: null }));
  };

  const submit = async () => {
    setPhase("processing");
    try {
      const r = await axios.post(`${API}/kyc/start`, {
        id_front_file_id: fileIds.id_front,
        id_back_file_id: fileIds.id_back,
        selfie_file_id: fileIds.selfie,
        document_type: "drivers_license",
      }, { withCredentials: true, timeout: API_REQUEST_TIMEOUT_MS });
      setResult(r.data);
      setPhase(r.data.status);
      if (r.data.status === "approved") setTimeout(() => { onApproved?.(); }, 1100);
    } catch (e) {
      toast.error(authRequestErrorMessage(e));
      setPhase("capture");
    }
  };

  const reset = () => {
    setFiles({ id_front: null, id_back: null, selfie: null });
    setFileIds({ id_front: null, id_back: null, selfie: null });
    setPhase("capture");
    setResult(null);
    setCaptureKey(null);
  };

  const captureOpen = !!captureKey;

  const handleDialogOpenChange = (nextOpen) => {
    if (!nextOpen && captureOpen) return;
    if (!nextOpen) onClose?.();
  };

  return (
    <>
    <Dialog open={open} onOpenChange={handleDialogOpenChange}>
      <DialogContent
        className="bg-white border border-[var(--zc-border)] max-w-2xl"
        data-testid="kyc-modal"
        onPointerDownOutside={(e) => { if (captureOpen) e.preventDefault(); }}
        onInteractOutside={(e) => { if (captureOpen) e.preventDefault(); }}
        onEscapeKeyDown={(e) => { if (captureOpen) e.preventDefault(); }}
      >
        <DialogHeader>
          <div className="flex items-center gap-2">
            <span className="w-9 h-9 rounded-full grid place-items-center bg-[var(--zc-accent-soft)] text-[var(--zc-accent)]">
              <ShieldCheck size={18} />
            </span>
            <DialogTitle className="zc-headline text-2xl">Verify your identity</DialogTitle>
          </div>
        </DialogHeader>

        {phase === "capture" && (
          <div data-testid="kyc-capture-step">
            <p className="text-sm text-[var(--zc-text-2)]">
              Verify with a valid driver&apos;s licence (front, back, and live selfie). You must be at least 24 to rent. Powered by Didit.
            </p>
            <div className="grid gap-3 mt-5 sm:grid-cols-3">
              {STEPS.map((s) => (
                <UploadTile
                  key={s.key}
                  step={s}
                  file={files[s.key]}
                  fileId={fileIds[s.key]}
                  uploading={uploading[s.key]}
                  onPick={(f) => pickFile(s.key, f)}
                  onClear={() => clearFile(s.key)}
                  onLiveCapture={() => setCaptureKey(s.key)}
                />
              ))}
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={onClose} className="px-4 h-10 zc-btn-ghost text-sm" data-testid="kyc-cancel">Cancel</button>
              <button
                onClick={submit}
                disabled={!requiredOk || Object.values(uploading).some(Boolean)}
                className="px-5 h-10 zc-btn-primary text-sm disabled:opacity-50"
                data-testid="kyc-submit"
              >
                Submit for verification
              </button>
            </div>
            <div className="zc-hint mt-3">
              Driver&apos;s licence front, back, and selfie are required. Renters must be 24 or older.
            </div>
          </div>
        )}

        {phase === "processing" && (
          <div className="py-12 text-center" data-testid="kyc-processing">
            <Loader2 size={32} className="mx-auto animate-spin text-[var(--zc-accent)]" />
            <div className="zc-stat text-2xl mt-4">Reviewing your documents…</div>
            <div className="text-sm text-[var(--zc-text-3)] mt-2">Verifying with Didit — usually under 10 seconds.</div>
          </div>
        )}

        {phase === "approved" && (
          <div className="py-10 text-center" data-testid="kyc-approved">
            <div className="w-14 h-14 mx-auto grid place-items-center rounded-full" style={{ background: "rgba(22,163,74,0.12)" }}>
              <CheckCircle2 size={28} className="text-[var(--zc-success)]" />
            </div>
            <div className="zc-stat text-2xl mt-4">Identity verified</div>
            <div className="text-sm text-[var(--zc-text-3)] mt-2">
              {result?.id_status ? `${result.id_status} · liveness ${(result.liveness_score * 100).toFixed(0)}%` : "All checks passed."}
            </div>
            <div className="text-xs text-[var(--zc-text-3)] mt-3">Continuing with your booking…</div>
          </div>
        )}

        {phase === "manual_review" && (
          <div className="py-10 text-center" data-testid="kyc-manual-review">
            <div className="w-14 h-14 mx-auto grid place-items-center rounded-full" style={{ background: "rgba(245,158,11,0.12)" }}>
              <Clock size={28} className="text-[var(--zc-warn,#f59e0b)]" />
            </div>
            <div className="zc-stat text-2xl mt-4">Manual review</div>
            <div className="text-sm text-[var(--zc-text-3)] mt-2 max-w-md mx-auto">
              {result?.review_reason || "Your documents need a quick human check."}{" "}
              We'll get back to you within 24 hours via email.
            </div>
            <button onClick={() => { onClose?.(); reset(); }} className="mt-6 px-5 h-10 zc-btn-primary text-sm" data-testid="kyc-mr-close">
              Got it
            </button>
          </div>
        )}

        {phase === "declined" && (
          <div className="py-10 text-center" data-testid="kyc-declined">
            <div className="w-14 h-14 mx-auto grid place-items-center rounded-full" style={{ background: "rgba(220,38,38,0.12)" }}>
              <AlertTriangle size={28} style={{ color: "var(--zc-danger,#dc2626)" }} />
            </div>
            <div className="zc-stat text-2xl mt-4">Verification failed</div>
            <div className="text-sm text-[var(--zc-text-3)] mt-2 max-w-md mx-auto">
              {result?.decline_reason || "We weren't able to verify your documents. Please try again with clearer photos in good lighting — avoid glare, blur, or cropped edges."}
            </div>
            <p className="text-xs text-[var(--zc-text-3)] mt-4 max-w-md mx-auto">
              Still stuck? Email{" "}
              <a href={`mailto:${SITE_EMAILS.support}`} className="text-[var(--zc-accent)] font-semibold">{SITE_EMAILS.support}</a>
              {" "}or visit the <Link to="/help" className="text-[var(--zc-accent)] font-semibold">help center</Link>.
            </p>
            <div className="flex justify-center gap-2 mt-6">
              <button onClick={() => { onClose?.(); }} className="px-4 h-10 zc-btn-ghost text-sm" data-testid="kyc-decl-close">Close</button>
              <button onClick={reset} className="px-5 h-10 zc-btn-primary text-sm" data-testid="kyc-decl-retry">Try again</button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
    {activeCapture && (
      <SelfieCapture
        open={!!captureKey}
        onClose={() => setCaptureKey(null)}
        onConfirm={onCaptureConfirmed}
        title={activeCapture.title}
        description={activeCapture.description}
        facingMode={activeCapture.facingMode}
        filenamePrefix={activeCapture.filenamePrefix}
        mirrorVideo={activeCapture.mirrorVideo}
        showFaceGuide={activeCapture.showFaceGuide}
        fallbackLabel={activeCapture.fallbackLabel}
        testIdPrefix={captureKey}
      />
    )}
    </>
  );
}
